import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    // Fallback to global notification if context not available
    return globalNotificationAPI;
  }
  return context;
};

const NotificationItem = ({ notification, onClose }) => {
  const { type, message, description, id } = notification;

  const bgColors = {
    success: 'bg-[rgba(156,255,206,0.15)] border-[rgba(156,255,206,0.4)]',
    warning: 'bg-[rgba(245,196,103,0.15)] border-[rgba(245,196,103,0.4)]',
    error: 'bg-[rgba(255,0,0,0.15)] border-[rgba(255,0,0,0.4)]',
    info: 'bg-[rgba(122,233,255,0.15)] border-[rgba(122,233,255,0.4)]',
  };

  const textColors = {
    success: 'text-[#9CFFCE]',
    warning: 'text-[#F5C467]',
    error: 'text-[rgba(255,0,0,0.8)]',
    info: 'text-[#7AE9FF]',
  };

  const icons = {
    success: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return (
    <div
      className={`${bgColors[type] || bgColors.info} ${textColors[type] || textColors.info} border rounded-xl p-4 mb-3 min-w-[320px] max-w-[480px] backdrop-blur-md shadow-lg`}
      style={{ animation: 'slideInRight 0.3s ease-out' }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {icons[type] || icons.info}
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold text-sm uppercase tracking-wider mb-1">
            {type === 'success' ? 'Congratulations' : type === 'warning' ? 'Warning' : type === 'error' ? 'Error' : 'Info'}
          </div>
          {description && (
            <div className="font-body text-sm text-[rgba(247,243,234,0.9)]">
              {description}
            </div>
          )}
          {message && !description && (
            <div className="font-body text-sm text-[rgba(247,243,234,0.9)]">
              {message}
            </div>
          )}
        </div>
        <button
          onClick={() => onClose(id)}
          className="flex-shrink-0 text-[rgba(247,243,234,0.6)] hover:text-[rgba(247,243,234,0.9)] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Global notification state
let globalNotifications = [];
let globalNotificationCallbacks = [];

const renderGlobalNotifications = () => {
  let container = document.getElementById('tailwind-notifications-global');
  if (!container) {
    container = document.createElement('div');
    container.id = 'tailwind-notifications-global';
    container.className = 'fixed top-4 right-4 z-[9999]';
    document.body.appendChild(container);
  }

  // Use React to render
  const ReactDOM = require('react-dom/client');
  const root = ReactDOM.createRoot(container);
  root.render(
    React.createElement(React.Fragment, null,
      globalNotifications.map((notif) =>
        React.createElement(NotificationItem, {
          key: notif.id,
          notification: notif,
          onClose: (id) => {
            globalNotifications = globalNotifications.filter((n) => n.id !== id);
            renderGlobalNotifications();
          }
        })
      )
    )
  );
};

const showGlobalNotification = (notification) => {
  const id = Date.now() + Math.random();
  const newNotification = { ...notification, id };
  globalNotifications = [...globalNotifications, newNotification];
  renderGlobalNotifications();

  setTimeout(() => {
    globalNotifications = globalNotifications.filter((n) => n.id !== id);
    renderGlobalNotifications();
  }, 4500);

  return id;
};

// Global API for backward compatibility
const globalNotificationAPI = {
  success: (config) => showGlobalNotification({ type: 'success', ...config }),
  warning: (config) => showGlobalNotification({ type: 'warning', ...config }),
  error: (config) => showGlobalNotification({ type: 'error', ...config }),
  info: (config) => showGlobalNotification({ type: 'info', ...config }),
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const showNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = { ...notification, id };
    setNotifications((prev) => [...prev, newNotification]);

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4500);

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const notification = {
    success: (config) => showNotification({ type: 'success', ...config }),
    warning: (config) => showNotification({ type: 'warning', ...config }),
    error: (config) => showNotification({ type: 'error', ...config }),
    info: (config) => showNotification({ type: 'info', ...config }),
  };

  return (
    <NotificationContext.Provider value={notification}>
      {children}
      {notifications.length > 0 && createPortal(
        <div className="fixed top-4 right-4 z-[9999]">
          {notifications.map((notif) => (
            <NotificationItem
              key={notif.id}
              notification={notif}
              onClose={removeNotification}
            />
          ))}
        </div>,
        document.body
      )}
    </NotificationContext.Provider>
  );
};

// Export notification API (uses global if provider not available)
export const notification = globalNotificationAPI;

export default notification;
